// --- Base Models (Output from API) ---

export interface UserModel {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  isActive: boolean;
  status: number; // 1=Pozvaný, 2=Aktivní, 3=Deaktivovaný
  password?: string; // Pouze pro Create
}

export interface PasswordUpdateModel {
  userId: string;
  newPassword: string;
}

// 1=Pozvaný, 2=Aktivní, 3=Deaktivovaný
export enum UserStatus {
  Invited = 1,
  Active = 2,
  Deactivated = 3
}

export interface InviteRequest {
  email: string;
  firstName: string;
  lastName: string;
  /** Když true, pošle se i pozvánkový e-mail; jinak se jen vygeneruje odkaz. */
  sendEmail?: boolean;
}

export interface InviteResponse {
  user: UserModel;
  inviteUrl: string;
  emailSent?: boolean;
}

export interface UserLiteModel {
  id: string;
  firstName: string;
  lastName: string;
}

export interface ClubModel {
  id: number;
  name: string;
  faNumber?: string;
  isInternal: boolean;
  logoUrl?: string;
}

/** Přestup mezi dvěma kluby + zařazení do přestupního období (pohled „Přestupy mezi kluby"). */
export interface TransferBetweenItem {
  transfer: TransferModel;
  periodId: string;
  periodLabel: string;
}

/** Jedna změna pole v auditu (u create jen new, u delete jen old, u update oboje). */
export interface AuditChange { field: string; old?: string | null; new?: string | null; }

export interface AuditLogModel {
  id: number;
  createdAt: string;
  userId?: string | null;
  userName?: string | null;
  userEmail?: string | null;
  action: number;
  actionLabel: string;
  entityType?: string | null;
  entityId?: string | null;
  summary: string;
  changes: AuditChange[];
  ipAddress?: string | null;
  success: boolean;
}

export interface PlayerModel {
  id: number;
  firstName: string;
  lastName: string;
  dateOfBirth?: string | Date;
  facrId?: string;
  currentClubId?: number;
  currentClub?: ClubModel;
  position?: string;
  isActive: boolean;
  transfers: TransferModel[];
  attachmentAssignments: AttachmentAssignmentModel[];
  created: string | Date;
  updated: string | Date;
}

export interface AttachmentModel {
  id?: number;
  fileName: string;
  storageId: string;
  mimeType: string;
  storageType: number;
  fileSize: number;
  uploadedAt: string | Date;
  url: string;
  thumbnailUrl?: string;
}

export interface AttachmentAssignmentModel {
  id?: number;
  attachmentId: number;
  attachment?: AttachmentModel;
  transferId?: number;
  playerId?: number;
  commentId?: number;
  assignedAt: string | Date;
}

export interface CommentModel {
  id?: number;
  content: string;
  createdAt: string | Date;
  authorUserId?: string;
  transferId?: number;

  user?: UserLiteModel;
}

export interface TransferModel {
  id: number;
  playerId: number;
  player?: PlayerModel;
  fromClubId: number;
  fromClub?: ClubModel;
  toClubId: number;
  toClub?: ClubModel;
  date: string | Date;
  type: TransferType;
  status: TransferStatus;
  loanEndDate?: string | Date;
  isLoanActive: boolean;
  feeType: FeeType;
  amount?: number;
  amountPerMatch?: number;
  currency: string;
  note?: string;
  previousTransferId?: number;
  linkType?: TransferLinkType;
  previousTransfer?: TransferLinkSummary;
  nextTransfer?: TransferLinkSummary;
  attachmentAssignments: AttachmentAssignmentModel[];
  comments: CommentModel[];
  created: string | Date;
  updated: string | Date;
}

export interface TransferLinkSummary {
  id: number;
  date: string | Date;
  type: TransferType;
  status: TransferStatus;
  linkType?: TransferLinkType;
  fromClubName?: string;
  toClubName?: string;
}

// --- Transfer Period ---

export interface TransferPeriod {
  id: string;
  label: string;
  startDate: string;
  endDate: string;
  isCurrent: boolean;
}

// --- Proposal (fronta návrhů z AI e-mail analýzy) ---

export interface ProposalModel {
  id: number;
  matchedTransferId?: number | null;
  confidence: number;
  proposedComment?: string | null;
  proposedFields?: string | null;       // JSON string (PascalCase klíče)
  attachmentIds?: string | null;
  attachments?: AttachmentModel[] | null;
  reasoning?: string | null;
  status: number;
  createdAt: string;
  /** Navrhovaná změna stavu přestupu (TransferStatus) — nejistá smluvní větev, schválení ji aplikuje na BE. */
  proposedStatus?: number | null;
  materskyKlubMatch?: ClubMatchInfo | null;
  novyKlubMatch?: ClubMatchInfo | null;
}

/** Shoda názvu klubu proti existujícím (zrcadlí BE ClubMatchInfo) — jen zobrazení. */
export interface ClubMatchInfo {
  matchedClubId?: number | null;
  matchedClubName?: string | null;
  scorePct: number;
  willAttach: boolean;
}

export interface ProposedFields {
  Typ?: string; Smer?: string; StrukturaPoplatku?: string;
  Type?: number; FeeType?: number; Amount?: number; Currency?: string;
  // string, dokud se parsuje ze zdrojového JSON (ProposalsComponent.parseFields); Date, jakmile jde
  // o lokální editovatelnou kopii svázanou s p-calendar (viz ProposalsComponent.editModel) — stejná
  // konvence jako TransferModel.date / loanEndDate.
  Date?: string | Date; LoanEndDate?: string | Date;
  PlayerName?: string; PlayerFacrId?: string; PlayerDateOfBirth?: string;
  MaterskyKlub?: ClubInfo; NovyKlub?: ClubInfo;
  OpceCastka?: number; Doplatky?: Doplatek[]; ProcentoZDalsihoProdeje?: number;
  MistoPodpisu?: string; DatumPodpisu?: string;
}
export interface ClubInfo { Nazev?: string; Ico?: string; Sidlo?: string; Zastupce?: string; }
export interface Doplatek { Kategorie?: string; Castka?: number; PocetUtkani?: number; MinutNaUtkani?: number; }

export interface ProposalEditPayload {
  matchedTransferId?: number | null;
  proposedComment?: string | null;
  proposedFields?: Partial<ProposedFields> | null;
}

// --- Transfer requests (schránka „Asistentka" — záložka Požadavky) ---
// Veřejně podaný požadavek na přestup (viz TransferRequestsController /
// TransferRequestService) + výsledek validace přiloženého souhlasu na pozadí.

export interface TransferRequestListModel {
  id: number;
  trainerName: string;
  trainerTeam: string;
  playerName: string;
  direction: number;                        // RequestDirection
  counterpartyClubName?: string | null;
  status: number;                           // TransferRequestStatus
  validationStatus: number;                 // ConsentValidationStatus
  createdAt: string;
}

export interface TransferRequestModel {
  id: number;
  trainerName: string;
  trainerTeam: string;
  note?: string | null;
  playerName: string;
  playerFacrId?: string | null;
  playerDateOfBirth?: string | null;
  direction: number;                        // RequestDirection
  counterpartyClubName?: string | null;
  counterpartyClubFacrId?: string | null;
  attachmentIds?: string | null;
  attachments?: AttachmentModel[] | null;    // dotažené BE (viz TransferRequestService.LoadAttachmentsAsync)
  status: number;                           // TransferRequestStatus
  validationStatus: number;                 // ConsentValidationStatus
  validationJson?: string | null;
  createdTransferId?: number | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * Rozparsovaný obsah `TransferRequestModel.validationJson` (viz ConsentValidationService.BuildJson
 * na backendu) — klíče jsou PascalCase, protože je serializuje čistý `JsonSerializer.Serialize`
 * (bez ASP.NET web-defaults, na rozdíl od zbytku API), ne camelCase jako ostatní modely tady.
 */
export interface ConsentValidationDetail {
  IsTransferConsent?: boolean;
  RoleExactlyOne?: boolean;
  PlayerName?: string;
  PlayerFacrId?: string;
  RodneCislo?: string;
  Adresa?: string;
  Email?: string;
  Telefon?: string;
  ClubName?: string;
  ClubFacrId?: string;
  Misto?: string;
  Datum?: string;
  SignaturePresent?: boolean;
  Confidence?: number;
  MissingFields?: string[];
  Mismatches?: string[];
}

// --- Todo (schránka „Asistentka" — záložka Úkoly) ---

export interface TodoModel {
  id: number;
  title: string;
  description?: string | null;
  status: number;      // TodoStatus
  source: number;       // TodoSource
  snoozedUntil?: string | null;
  transferId?: number | null;
  playerId?: number | null;
  completedAt?: string | null;
  createdAt: string;

  // Kontext navázaného přestupu/hráče (BE joinuje přes navigation properties)
  playerName?: string | null;
  fromClubName?: string | null;
  toClubName?: string | null;
  fromClubIsInternal?: boolean;
  toClubIsInternal?: boolean;
  transferType?: number | null;    // TransferType
  transferStatus?: number | null;  // TransferStatus
  transferDate?: string | null;
}

export interface TodoCreatePayload {
  title: string;
  description?: string | null;
  transferId?: number | null;
  playerId?: number | null;
}

// --- Knowledge (stránka „Znalosti" — pokyny a fakta pro AI e-mail analyzer) ---

export interface KnowledgeModel {
  id: number;
  kind: number;         // KnowledgeKind
  title?: string | null;
  content: string;
  isActive: boolean;
  createdAt: string;
  source?: number;       // KnowledgeSource
  reviewStatus?: number; // KnowledgeReviewStatus
  rationale?: string;
}

export interface KnowledgeCreatePayload {
  kind: number;         // KnowledgeKind
  title?: string | null;
  content: string;
}

export interface KnowledgeUpdatePayload {
  title?: string | null;
  content: string;
  isActive: boolean;
}

// --- Question (schránka „Asistentka" — záložka Otázky) ---

export interface QuestionOption {
  label: string;
  value: string;
}

export interface QuestionModel {
  id: number;
  questionType: number;   // QuestionType
  proposalId: number | null;
  text: string;
  options: QuestionOption[];
  status: number;          // QuestionStatus
  answerValue?: string | null;
  createdAt: string;
  context?: string | null; // čitelný přehled toho, co AI z e-mailu pochopila
}

// --- Activity (schránka „Asistentka" — záložka Aktivita, jen ke čtení) ---

export interface ActivityModel {
  id: number;
  category: number;    // ActivityCategory
  level: number;        // ActivityLevel
  message: string;
  processedEmailId?: number | null;
  proposalId?: number | null;
  transferId?: number | null;
  createdAt: string;
}

export interface ActivityPage {
  items: ActivityModel[];
  total: number;
  page: number;
  pageSize: number;
}

// --- Decision (schránka „Asistentka" — záložka Deník, jen ke čtení) ---
// Zrcadlí BE ProposedTransferFields (EmailAnalysisResult.cs) přes System.Text.Json
// camelCase serializaci — pozor, JINÉ klíče než ProposedFields výše (ta je PascalCase
// kopie stejného tvaru pro Proposal.proposedFields, který se parsuje ručně ze string pole).
export interface DecisionProposedFields {
  playerName?: string;
  typ?: string;
  smer?: string;
  amount?: number;
  currency?: string;
  date?: string;
  [key: string]: any;
}

export interface DecisionItem {
  id: number;
  decisionType: number;   // DecisionType
  decisionTypeLabel: string;
  proposalId?: number;
  transferId?: number;
  aiProposed?: DecisionProposedFields;
  userFinal?: DecisionProposedFields;
  createdAt: string;
}

export interface DecisionList {
  items: DecisionItem[];
  total: number;
}

export interface DecisionStats {
  total: number;
  approve: number;
  edit: number;
  reject: number;
  auto: number;
  answer: number;
  humanVerdicts: number;
  agreementRate: number | null;
  // Metrika učení (počítá se po návrzích, ne po lozích) + měsíční trend.
  approvedAsIs: number;
  editedProposals: number;
  rejectedProposals: number;
  approveAsIsRate: number | null;
  trend: DecisionTrendBucket[];
}

export interface DecisionTrendBucket {
  period: string;   // "yyyy-MM"
  approvedAsIs: number;
  edited: number;
  rejected: number;
  rate: number | null;
}

// --- Import (zpětný hromadný import přestupů ze složek — viz ImportController /
// TransferManager.Core/Models/ImportModels.cs). Standardní System.Text.Json camelCase
// serializace (na rozdíl od Proposal.proposedFields, který je string parsovaný ručně).

export interface ImportClubInfoModel {
  nazev?: string;
  ico?: string;
  sidlo?: string;
  zastupce?: string;
}

export interface ImportDoplatekModel {
  kategorie?: string;
  castka?: number;
  pocetUtkani?: number;
  minutNaUtkani?: number;
}

/** Zrcadlí BE ProposedTransferFields (EmailAnalysisResult.cs), sdílenou i ImportDraftModel.ProposedFields. */
export interface ProposedTransferFieldsModel {
  typ?: string;                 // "prestup" | "hostovani"
  smer?: string;                 // "k nam" | "pryc"
  strukturaPoplatku?: string;
  type?: number;                 // TransferType
  feeType?: number;              // FeeType
  amount?: number;
  currency?: string;
  date?: string;
  loanEndDate?: string;
  playerName?: string;
  playerFacrId?: string;
  playerDateOfBirth?: string;
  materskyKlub?: ImportClubInfoModel;
  novyKlub?: ImportClubInfoModel;
  opceCastka?: number;
  doplatky?: ImportDoplatekModel[];
  procentoZDalsihoProdeje?: number;
  mistoPodpisu?: string;
  datumPodpisu?: string;
}

/** Zrcadlí BE ImportModelOption (ImportOptions.AvailableModels) — jedna položka dropdownu AI modelu. */
export interface ImportModelOption {
  id: string;
  label: string;
  priceHint?: string | null;
}

export interface ImportRunModel {
  id: number;
  rootPath: string;
  model?: string | null;
  status: number;                // ImportRunStatus
  totalFolders: number;
  processedFolders: number;
  errorFolders: number;
  createdAt: string;
}

export interface ImportDraftModel {
  id: number;
  importItemId: number;
  detectedPlayerName?: string | null;
  confidence: number;
  proposedFields?: ProposedTransferFieldsModel | null;
  documentAttachmentIds: number[];
  status: number;                 // ImportDraftStatus
  duplicateTransferId?: number | null;
  resolvedPlayerId?: number | null;
  resolvedFromClubId?: number | null;
  resolvedToClubId?: number | null;
  createdTransferId?: number | null;
  /** Hráč a/nebo klub se nepodařilo jednoznačně dohledat — vyžaduje ruční ověření, vyřazuje z hromadného schválení. */
  needsConfirm: boolean;
}

/** Payload pro `POST api/import/drafts/{id}/edit` (viz EditDraftRequest na BE). */
export interface ImportEditPayload {
  fields: ProposedTransferFieldsModel;
  playerId?: number | null;
  fromClubId?: number | null;
  toClubId?: number | null;
}

// --- DataListResponse (Generic response wrapper) ---

export interface DataListResponse<T> {
  data: T[];
  totalRecords: number;
  page: number;
  pageSize: number;
}

// --- Enums ---

export enum StorageType {
  Local = 1,
  GoogleDrive = 2
}

export enum AttachmentOwnerType {
  Transfer = 1,
  Player = 2,
  Comment = 3
}

export enum TransferType {
  Permanent = 1,
  Loan = 2,
  CooperationAgreement = 3,
  Unspecified = 4
}

export enum FeeType {
  Free = 1,
  Fixed = 2,
  PerMatch = 3
}

/** Zrcadlí BE TransferManager.DAL.Enums.ImportRunStatus (viz ImportRunModel.status). */
export enum ImportRunStatus {
  Running = 1,
  Completed = 2,
  Failed = 3
}

/** Zrcadlí BE TransferManager.DAL.Enums.ImportDraftStatus (viz ImportDraftModel.status). */
export enum ImportDraftStatus {
  Pending = 1,
  Approved = 2,
  Edited = 3,
  Rejected = 4,
  Error = 5
}

export enum TransferStatus {
  ToResolve = 1,
  PendingOtherSide = 2,
  PendingOurSignatures = 3,
  PendingFacrConfirmation = 4,
  Resolved = 5,
  Cancelled = 6
}

export enum TransferLinkType {
  LoanToPermanent = 1,
  LoanExtension = 2,
  LoanReturn = 3,
  SubsequentTransfer = 4
}

export enum TodoStatus {
  Open = 1,
  Done = 2,
  Dismissed = 3,
  AutoClosed = 4
}

export enum TodoSource {
  Manual = 1,
  Rule = 2,
  Ai = 3
}

export enum KnowledgeKind {
  Instruction = 1,
  Fact = 2
}

export enum QuestionType {
  SelectTransfer = 1,
  ConfirmClassification = 2
}

export enum QuestionStatus {
  Open = 1,
  Answered = 2,
  Dismissed = 3
}

export enum ActivityCategory {
  EmailLifecycle = 1,
  Result = 2,
  CreditProtection = 3,
  HumanDecision = 4
}

export enum ActivityLevel {
  Info = 1,
  Warning = 2,
  Error = 3
}

export enum DecisionType {
  Approve = 1,
  Edit = 2,
  Reject = 3,
  Auto = 4,
  Answer = 5
}

export enum RequestDirection {
  ToUs = 1,
  Away = 2
}

export enum TransferRequestStatus {
  New = 1,
  Validated = 2,
  Converted = 3,
  Rejected = 4
}

export enum ConsentValidationStatus {
  Pending = 1,
  Valid = 2,
  Issues = 3,
  Error = 4
}

// --- Price tables (stránka „Přestupní řád" — tabulkové odstupné, § 22-25) ---

export enum PriceCategory {
  Men = 1,
  YouthBoys = 2,
  Women = 3,
  Girls = 4
}

export enum PriceDirection {
  /** Příchod — hráč přichází k nám (naše 1. liga, soutěž protistrany irelevantní). */
  Incoming = 1,
  /** Odchod — hráč odchází od nás (průměr naší 1. ligy a soutěže protistrany). */
  Outgoing = 2
}

export interface PriceTableLeague {
  /** Pořadí 1..n, 1 = nejvyšší soutěž = náš klub (backend s tím počítá napevno). */
  rank: number;
  name: string;
}

export interface PriceTableColumn {
  label: string;
  minAge: number;
  maxAge: number;
}

/** Celá editovatelná struktura jedné tabulky. cells[rowIndex][colIndex]: řádky = leagues, sloupce = columns (stejné pořadí). */
export interface PriceTableStructure {
  leagues: PriceTableLeague[];
  columns: PriceTableColumn[];
  cells: number[][];
}

export interface PriceTableModel {
  category: PriceCategory;
  structure: PriceTableStructure;
  updatedAt: string;
}

export interface PriceCalculateRequest {
  category: PriceCategory;
  birthYear: number;
  /** Formát YYYY-MM-DD; nepovinné, BE bere dnešek jako fallback. */
  referenceDate?: string | null;
  direction: PriceDirection;
  /** Rank soutěže protistrany — vyžadováno jen u Outgoing. */
  otherLeagueRank?: number | null;
  /** Rank naší soutěže (výchozí 1 = nejvyšší). Nově volitelný — dřív se bral napevno 1. */
  ourLeagueRank?: number | null;
}

export interface PriceResult {
  amount: number | null;
  ageNumber: number;
  columnLabel: string | null;
  outOfRange: boolean;
  explanation: string;
}

export interface EmailListItem {
  id: number;
  subject?: string;
  fromAddress?: string;
  date: string;
  analysisStatus: number;
  attachmentCount: number;
  linkedTransferCount: number;
}

export interface EmailLinkedTransfer {
  transferId: number;
  playerName: string;
  fromClubName: string;
  toClubName: string;
  status: number;
  linkSource: number;
}

export interface EmailDetail {
  id: number;
  subject?: string;
  fromAddress?: string;
  date: string;
  body?: string;
  analysisStatus: number;
  attachments: AttachmentModel[];
  linkedTransfers: EmailLinkedTransfer[];
}

export interface EmailListResponse {
  data: EmailListItem[];
  totalRecords: number;
  page: number;
  pageSize: number;
}

// ============================================
// VZORY SMLUV (Contract templates)
// ============================================

/**
 * Vzor smlouvy (.docx) — název souboru + rozpoznané štítky (klub/typ/směr/varianta) a stav
 * aktivace. Enumy chodí z BE jako int (viz ContractTemplateModel na BE, T12). Nerozpoznaný
 * nahraný vzor přijde s klub=0/typ=0 ("nezařazeno") — uživatel doštítkuje přes PUT tags.
 */
export interface ContractTemplateModel {
  id: number;
  fileName: string;
  klub: number;
  typ: number;
  smer: number;
  varianta: number;
  isActive: boolean;
  uploadedAt: string;
}

/** Tělo PUT `/api/contract-templates/{id}/tags` (viz ContractTemplatesController.UpdateTagsRequest na BE). */
export interface ContractTemplateTags {
  klub: number;
  typ: number;
  smer: number;
  varianta: number;
}

// ============================================
// GENEROVÁNÍ SMLUV (Contract generation — T14)
// ============================================

/** Popis jedné značky ve vzoru smlouvy, jejíž hodnota chybí (viz ContractFieldDescriptor na BE, T12). */
export interface ContractFieldDescriptor {
  label: string;
  humanLabel: string;
  kind: number;
  target: number;
  fieldKey: string;
}

/**
 * Odhad vzoru smlouvy odvozený z přestupu (viz ContractMatchSuggestion na BE). `klub` je
 * `null`, pokud jsou oba kluby přestupu interní a nejde jednoznačně určit náš klub
 * (`ambiguousClub`) — pak musí uživatel vybrat vzor ručně.
 */
export interface ContractMatchSuggestion {
  klub: number | null;
  smer: number;
  typ: number;
  variantGuess: number;
  suggestedTemplateId: number | null;
  ambiguousClub: boolean;
  candidateTemplateIds: number[];
}

/** Výsledek `GET /api/contracts/suggest/{transferId}` (viz ContractSuggestResult na BE). */
export interface ContractSuggestResult {
  suggestion: ContractMatchSuggestion;
  missing: ContractFieldDescriptor[];
}

/** Jedna zpráva v konverzaci s AI asistentem pro doplnění polí smlouvy (role "user"/"assistant"). */
export interface ContractChatMessage {
  role: string;
  text: string;
}

/** Hodnota jednoho pole smlouvy extrahovaná AI asistentem z odpovědi uživatele (surový text). */
export interface ContractFieldValue {
  fieldKey: string;
  rawValue: string;
}

/** Výsledek jednoho kroku konverzace `POST /api/contracts/assistant` (viz ContractAssistantTurn na BE). */
export interface ContractAssistantTurn {
  assistantMessage: string;
  extracted: ContractFieldValue[];
  complete: boolean;
}

/** Tělo `POST /api/contracts/assistant`. */
export interface ContractAssistantRequest {
  transferId: number;
  templateId: number;
  history: ContractChatMessage[];
}

/** Výsledek dohledání klubu v rejstříku `POST /api/club-registry/lookup` (viz ClubRegistryResult na BE). Kterékoli pole může chybět (best-effort dohledání). */
export interface ClubRegistryResult {
  ico?: string | null;
  sidlo?: string | null;
  name?: string | null;
  zastupceJmeno?: string | null;
  zastupceFunkce?: string | null;
}

